class CarouselAnalyticsTracker {
  constructor() {
    this.seenLists = new Set();
    this.observer = null;
    this.mutationObserver = null;
    this.tabObserver = null;
  }

  getListPayload(listId) {
    const script = [...document.querySelectorAll('script[data-tracking-list-id]')]
      .find(element => element.dataset.trackingListId === listId);
    if (!script) return null;

    try {
      const data = JSON.parse(script.textContent);
      if (!Array.isArray(data.items)) return null;

      return {
        item_list_id: listId,
        item_list_name: data.list_name || listId,
        items: data.items
      };
    } catch (e) {
      console.warn(`Invalid JSON in carousel script for "${listId}"`, e);
      return null;
    }
  }

  publishViewItemList(payload) {
    if (window.Shopify && typeof window.Shopify?.analytics?.publish === 'function') {
      window.Shopify.analytics.publish('forix:item_list', payload);
    } else {
      console.warn('Shopify analytics not ready.');
    }
  }

  publishViewSearch(payload) {
    if (window.Shopify && typeof window.Shopify.analytics?.publish === 'function'){
      window.Shopify.analytics.publish('search', payload);
      // window.Shopify.analytics.publish('view_search_results', payload);
    } else {
      console.warn('Shopify analytics not ready.');
    }
  }

  observeCarousels() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        // Inactive tabs may only be hidden via visibility/opacity (still intersecting), so wait until they are selected
        if (entry.target.closest('.tab__content-item:not(.active)')) return;
        const listId = entry.target.dataset.trackingListId;
        if (!listId || this.seenLists.has(listId)) return;
        const payload = this.getListPayload(listId);
        if (payload) {
          this.publishViewItemList(payload);
          this.seenLists.add(listId);
        }
      });
    }, { threshold: 0 });

    this.observeExistingElements();
    this.startMutationObserver();
    this.observeTabs();
  }

  observeTabs() {
    this.tabObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ target }) => {
        if (!target.classList.contains('active')) return;
        // Re-observe so the IntersectionObserver re-evaluates lists that were skipped while the tab was inactive
        target.querySelectorAll('div[data-tracking-list-id]').forEach(element => {
          if (this.seenLists.has(element.dataset.trackingListId)) return;
          this.observer.unobserve(element);
          this.observer.observe(element);
        });
        if (target.matches('div[data-tracking-list-id]') && !this.seenLists.has(target.dataset.trackingListId)) {
          this.observer.unobserve(target);
          this.observer.observe(target);
        }
      });
    });

    document.querySelectorAll('.tab__content-item').forEach(tab => {
      this.tabObserver.observe(tab, { attributes: true, attributeFilter: ['class'] });
    });
  }

  observeSearch() {
    const searchQuery = this.getQueryParam('_psq') || this.getQueryParam('q');
    if (searchQuery && !window.location.pathname.includes('/search')) {
      const payload = { search_term: searchQuery };
      this.publishViewSearch(payload);
    }
  }

  observeExistingElements() {
    document.querySelectorAll('div[data-tracking-list-id]').forEach(element => this.observer.observe(element));
  }

  startMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches && node.matches('div[data-tracking-list-id]')) this.observeElement(node);
            const childrenWithTracking = node.querySelectorAll && node.querySelectorAll('div[data-tracking-list-id]');
            if (childrenWithTracking) childrenWithTracking.forEach(child => this.observeElement(child));
          }
        });
      });
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  observeElement(element) {
    const listId = element.dataset.trackingListId;
    if (listId && !this.seenLists.has(listId)) this.observer.observe(element);
  }

  getQueryParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.observeSearch();
        this.observeCarousels();
      });
    } else {
      this.observeSearch();
      this.observeCarousels();
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    if (this.tabObserver) {
      this.tabObserver.disconnect();
      this.tabObserver = null;
    }
    this.seenLists.clear();
  }
}

const carouselTracker = new CarouselAnalyticsTracker();
carouselTracker.init();